import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
export function TermsOfServicePage() {
  return <>
      <Helmet data-id="element-10144">
        <title data-id="element-10145">Terms of Service | ToxicDetector</title>
        <meta name="description" content="Read the terms and conditions for using ToxicDetector's AI-powered message analysis tools." data-id="element-10146" />
        <link rel="canonical" href="https://toxicdetector.com/terms" data-id="element-10147" />
        <meta property="og:title" content="Terms of Service | ToxicDetector" data-id="element-10148" />
        <meta property="og:description" content="Read the terms and conditions for using ToxicDetector's AI-powered message analysis tools." data-id="element-10149" />
        <meta property="og:url" content="https://toxicdetector.com/terms" data-id="element-10150" />
        <meta property="og:type" content="website" data-id="element-10151" />
        <meta name="robots" content="index, follow" data-id="element-10152" />
      </Helmet>
      <div className="flex flex-col w-full min-h-screen bg-[#f8f6f2]" data-id="element-10153">
        <header className="bg-white border-b border-gray-200" data-id="element-10154">
          <div className="max-w-4xl mx-auto px-5 py-4" data-id="element-10155">
            <Link to="/" className="inline-flex items-center text-gray-600 hover:text-purple-600 text-sm mb-2" data-id="element-10156">
              <ArrowLeft size={16} className="mr-1" data-id="element-10157" />
              Back to Home
            </Link>
            <h1 className="text-2xl font-bold" data-id="element-10158">Terms of Service</h1>
            <p className="text-gray-600 text-sm mt-1" data-id="element-10159">
              Last updated:{' '}
              {new Date().toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
            </p>
          </div>
        </header>

        <div className="max-w-4xl mx-auto w-full px-5 py-8" data-id="element-10160">
          <div className="bg-white rounded-xl p-8 shadow-sm" data-id="element-10161">
            <section className="mb-8" data-id="element-10162">
              <h2 className="text-xl font-bold mb-4" data-id="element-10163">Agreement to Terms</h2>
              <p className="text-gray-700 mb-4" data-id="element-10164">
                Welcome to ToxicDetector. These Terms of Service ("Terms")
                govern your access to and use of our website, application, and
                services (collectively, the "Services"). By accessing or using
                our Services, you agree to be bound by these Terms and our
                Privacy Policy.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-10165">
                If you do not agree to these Terms, you may not access or use
                our Services. We reserve the right to modify these Terms at any
                time, and your continued use of the Services constitutes
                acceptance of any changes.
              </p>
              <p className="text-gray-700" data-id="element-10166">
                <strong data-id="element-10167">IMPORTANT:</strong> These Terms contain an arbitration
                clause and class action waiver that affect your legal rights.
                Please read Section 14 carefully.
              </p>
            </section>

            <section className="mb-8" data-id="element-10168">
              <h2 className="text-xl font-bold mb-4" data-id="element-10169">
                1. Description of Services
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-10170">
                ToxicDetector is an AI-powered platform that analyzes text
                conversations for patterns of toxic behavior, including but not
                limited to gaslighting, manipulation, emotional abuse, and other
                harmful communication patterns. Our Services include:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10171">
                <li data-id="element-10172">Screenshot upload and secure storage</li>
                <li data-id="element-10173">
                  AI-powered toxicity analysis using advanced natural language
                  processing
                </li>
                <li data-id="element-10174">Detailed reports on detected patterns and risk levels</li>
                <li data-id="element-10175">Scan history and result management</li>
                <li data-id="element-10176">Result sharing capabilities</li>
                <li data-id="element-10177">User account management and privacy controls</li>
              </ul>
              <p className="text-gray-700" data-id="element-10178">
                ToxicDetector is provided for informational and educational
                purposes only. Our analysis is not a substitute for professional
                advice from qualified mental health professionals, therapists,
                counselors, or legal advisors.
              </p>
            </section>

            <section className="mb-8" data-id="element-10179">
              <h2 className="text-xl font-bold mb-4" data-id="element-10180">
                2. Eligibility and Account Registration
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10181">
                2.1 Age Requirements
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10182">
                You must be at least 18 years old to use ToxicDetector. By using
                our Services, you represent and warrant that you are at least 18
                years of age and have the legal capacity to enter into these
                Terms.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10183">
                2.2 Account Creation
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10184">
                To access certain features, you must create an account. You
                agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10185">
                <li data-id="element-10186">
                  Provide accurate, current, and complete information during
                  registration
                </li>
                <li data-id="element-10187">Maintain and promptly update your account information</li>
                <li data-id="element-10188">
                  Maintain the security of your password and account credentials
                </li>
                <li data-id="element-10189">
                  Accept responsibility for all activities that occur under your
                  account
                </li>
                <li data-id="element-10190">
                  Notify us immediately of any unauthorized access or security
                  breach
                </li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10191">
                2.3 Account Security
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10192">
                You are solely responsible for maintaining the confidentiality
                of your account credentials. We strongly recommend enabling
                two-factor authentication (2FA) for enhanced security. You agree
                not to share your account with others or allow others to access
                your account.
              </p>
            </section>

            <section className="mb-8" data-id="element-10193">
              <h2 className="text-xl font-bold mb-4" data-id="element-10194">
                3. Acceptable Use Policy
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10195">3.1 Permitted Uses</h3>
              <p className="text-gray-700 mb-4" data-id="element-10196">
                You may use ToxicDetector only for lawful purposes and in
                accordance with these Terms. You agree to use the Services only
                to analyze conversations to which you are a party or for which
                you have obtained proper consent.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10197">
                3.2 Prohibited Uses
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10198">You agree NOT to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10199">
                <li data-id="element-10200">
                  Upload conversations or content that you do not have the right
                  to analyze
                </li>
                <li data-id="element-10201">
                  Violate any applicable laws, regulations, or third-party
                  rights
                </li>
                <li data-id="element-10202">
                  Upload content containing child exploitation material, illegal
                  content, or content that violates others' privacy
                </li>
                <li data-id="element-10203">
                  Use the Services to harass, abuse, stalk, threaten, or
                  intimidate others
                </li>
                <li data-id="element-10204">
                  Attempt to gain unauthorized access to our systems or other
                  users' accounts
                </li>
                <li data-id="element-10205">
                  Reverse engineer, decompile, or attempt to extract source code
                  from our Services
                </li>
                <li data-id="element-10206">
                  Use automated systems (bots, scrapers) to access the Services
                  without permission
                </li>
                <li data-id="element-10207">Interfere with or disrupt the Services or servers</li>
                <li data-id="element-10208">
                  Impersonate any person or entity or misrepresent your
                  affiliation
                </li>
                <li data-id="element-10209">
                  Use the Services for any commercial purpose without our
                  written consent
                </li>
                <li data-id="element-10210">Remove, obscure, or alter any legal notices or branding</li>
              </ul>
            </section>

            <section className="mb-8" data-id="element-10211">
              <h2 className="text-xl font-bold mb-4" data-id="element-10212">
                4. User Content and Consent
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10213">4.1 Your Content</h3>
              <p className="text-gray-700 mb-4" data-id="element-10214">
                You retain all ownership rights to the content you upload ("User
                Content"). By uploading content to ToxicDetector, you represent
                and warrant that:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10215">
                <li data-id="element-10216">
                  You own or have the necessary rights to upload and analyze the
                  content
                </li>
                <li data-id="element-10217">
                  The content does not violate any laws or third-party rights
                </li>
                <li data-id="element-10218">
                  You have obtained all necessary consents from other parties in
                  the conversation
                </li>
                <li data-id="element-10219">
                  The content does not contain illegal material or violate our
                  Acceptable Use Policy
                </li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10220">4.2 License Grant</h3>
              <p className="text-gray-700 mb-4" data-id="element-10221">
                By uploading User Content, you grant ToxicDetector a limited,
                non-exclusive, worldwide, royalty-free license to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10222">
                <li data-id="element-10223">Store and process your content to provide the Services</li>
                <li data-id="element-10224">Perform AI analysis on your content</li>
                <li data-id="element-10225">Display analysis results to you</li>
                <li data-id="element-10226">
                  Create aggregated, anonymized data for service improvement (no
                  personal identifiers)
                </li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-10227">
                This license terminates when you delete your content or account,
                except for content you have chosen to share publicly.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10228">
                4.3 Content Removal
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10229">
                We reserve the right to remove any User Content that violates
                these Terms, our policies, or applicable law. We may also
                suspend or terminate accounts that repeatedly violate our
                policies.
              </p>
            </section>

            <section className="mb-8" data-id="element-10230">
              <h2 className="text-xl font-bold mb-4" data-id="element-10231">
                5. Intellectual Property Rights
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10232">
                5.1 Our Intellectual Property
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10233">
                ToxicDetector and all associated content, features,
                functionality, software, algorithms, designs, logos, and
                trademarks are owned by ToxicDetector and are protected by
                copyright, trademark, patent, and other intellectual property
                laws.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-10234">
                <strong data-id="element-10235">
                  © {new Date().getFullYear()} ToxicDetector. All rights
                  reserved.
                </strong>
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10236">
                5.2 Limited License
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10237">
                Subject to these Terms, we grant you a limited, non-exclusive,
                non-transferable, revocable license to access and use the
                Services for your personal, non-commercial use. This license
                does not include any right to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10238">
                <li data-id="element-10239">Resell or make commercial use of the Services</li>
                <li data-id="element-10240">Modify, copy, or create derivative works</li>
                <li data-id="element-10241">
                  Download or extract any portion of the Services (except as
                  expressly permitted)
                </li>
                <li data-id="element-10242">
                  Use any data mining, robots, or similar data gathering tools
                </li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10243">5.3 Feedback</h3>
              <p className="text-gray-700 mb-4" data-id="element-10244">
                If you provide feedback, suggestions, or ideas about our
                Services, you grant us the right to use such feedback without
                compensation or attribution.
              </p>
            </section>

            <section className="mb-8" data-id="element-10245">
              <h2 className="text-xl font-bold mb-4" data-id="element-10246">
                6. AI Analysis and Accuracy
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10247">
                6.1 Nature of AI Analysis
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10248">
                Our toxicity analysis is powered by artificial intelligence and
                machine learning algorithms. While we strive for accuracy, AI
                analysis has inherent limitations and may not always be 100%
                accurate or complete.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10249">6.2 No Guarantees</h3>
              <p className="text-gray-700 mb-4" data-id="element-10250">
                We do not guarantee that our analysis will:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10251">
                <li data-id="element-10252">Detect all instances of toxic behavior</li>
                <li data-id="element-10253">Be free from false positives or false negatives</li>
                <li data-id="element-10254">
                  Account for context, sarcasm, cultural differences, or nuanced
                  communication
                </li>
                <li data-id="element-10255">
                  Provide legally admissible evidence or professional
                  psychological assessment
                </li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10256">
                6.3 Not Professional Advice
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10257">
                <strong data-id="element-10258">IMPORTANT:</strong> ToxicDetector's analysis is for
                informational purposes only and does not constitute professional
                advice. Our Services are not a substitute for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10259">
                <li data-id="element-10260">Mental health counseling or therapy</li>
                <li data-id="element-10261">Legal advice or representation</li>
                <li data-id="element-10262">Medical diagnosis or treatment</li>
                <li data-id="element-10263">Professional relationship counseling</li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-10264">
                If you are experiencing abuse, harassment, or mental health
                concerns, please seek help from qualified professionals, law
                enforcement, or support organizations.
              </p>
            </section>

            <section className="mb-8" data-id="element-10265">
              <h2 className="text-xl font-bold mb-4" data-id="element-10266">
                7. Privacy and Data Protection
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-10267">
                Your privacy is important to us. Our collection, use, and
                protection of your personal information is governed by our
                Privacy Policy, which is incorporated into these Terms by
                reference. By using our Services, you consent to our Privacy
                Policy.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-10268">Key privacy points:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10269">
                <li data-id="element-10270">We encrypt all data in transit and at rest</li>
                <li data-id="element-10271">You control your data retention settings</li>
                <li data-id="element-10272">You can delete your data at any time</li>
                <li data-id="element-10273">We do not sell your personal information</li>
                <li data-id="element-10274">
                  We comply with GDPR, CCPA, and other privacy regulations
                </li>
              </ul>
            </section>

            <section className="mb-8" data-id="element-10275">
              <h2 className="text-xl font-bold mb-4" data-id="element-10276">
                8. Disclaimers and Limitations of Liability
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10277">
                8.1 Service Provided "As Is"
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10278">
                THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS
                WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
                INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY,
                FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR ACCURACY.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10279">8.2 No Warranty</h3>
              <p className="text-gray-700 mb-4" data-id="element-10280">We do not warrant that:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10281">
                <li data-id="element-10282">
                  The Services will be uninterrupted, timely, secure, or
                  error-free
                </li>
                <li data-id="element-10283">
                  The results obtained from using the Services will be accurate
                  or reliable
                </li>
                <li data-id="element-10284">Any errors in the Services will be corrected</li>
                <li data-id="element-10285">The Services will meet your specific requirements</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10286">
                8.3 Limitation of Liability
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10287">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, TOXICDETECTOR SHALL NOT
                BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
                OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER
                INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE,
                GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10288">
                <li data-id="element-10289">Your use or inability to use the Services</li>
                <li data-id="element-10290">
                  Any unauthorized access to or use of our servers and/or any
                  personal information stored therein
                </li>
                <li data-id="element-10291">
                  Any interruption or cessation of transmission to or from the
                  Services
                </li>
                <li data-id="element-10292">
                  Any bugs, viruses, or similar issues transmitted through the
                  Services
                </li>
                <li data-id="element-10293">
                  Any errors or omissions in any content or for any loss or
                  damage incurred as a result of the use of any content posted,
                  emailed, transmitted, or otherwise made available through the
                  Services
                </li>
                <li data-id="element-10294">
                  The accuracy or reliability of any AI analysis or results
                </li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-10295">
                IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL DAMAGES,
                LOSSES, AND CAUSES OF ACTION EXCEED THE AMOUNT PAID BY YOU, IF
                ANY, FOR ACCESSING THE SERVICES IN THE TWELVE (12) MONTHS PRIOR
                TO THE CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS
                GREATER.
              </p>
            </section>

            <section className="mb-8" data-id="element-10296">
              <h2 className="text-xl font-bold mb-4" data-id="element-10297">9. Indemnification</h2>
              <p className="text-gray-700 mb-4" data-id="element-10298">
                You agree to indemnify, defend, and hold harmless ToxicDetector,
                its officers, directors, employees, agents, and affiliates from
                and against any claims, liabilities, damages, losses, costs,
                expenses, or fees (including reasonable attorneys' fees) arising
                from:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10299">
                <li data-id="element-10300">Your use or misuse of the Services</li>
                <li data-id="element-10301">Your violation of these Terms</li>
                <li data-id="element-10302">Your violation of any rights of another party</li>
                <li data-id="element-10303">Your User Content</li>
                <li data-id="element-10304">Your violation of any applicable laws or regulations</li>
              </ul>
            </section>

            <section className="mb-8" data-id="element-10305">
              <h2 className="text-xl font-bold mb-4" data-id="element-10306">10. Termination</h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10307">
                10.1 Termination by You
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10308">
                You may terminate your account at any time by accessing your
                Settings and selecting the account deletion option. Upon
                termination, your access to the Services will cease immediately.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10309">
                10.2 Termination by Us
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10310">
                We reserve the right to suspend or terminate your account and
                access to the Services at any time, with or without notice, for
                any reason, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10311">
                <li data-id="element-10312">Violation of these Terms or our policies</li>
                <li data-id="element-10313">Fraudulent, abusive, or illegal activity</li>
                <li data-id="element-10314">Extended periods of inactivity</li>
                <li data-id="element-10315">Technical or security reasons</li>
                <li data-id="element-10316">At our sole discretion</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10317">
                10.3 Effect of Termination
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10318">Upon termination:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10319">
                <li data-id="element-10320">Your right to use the Services will immediately cease</li>
                <li data-id="element-10321">
                  We will delete your account data according to our data
                  retention policy
                </li>
                <li data-id="element-10322">
                  Provisions that by their nature should survive termination
                  will remain in effect
                </li>
                <li data-id="element-10323">
                  You remain liable for any obligations incurred prior to
                  termination
                </li>
              </ul>
            </section>

            <section className="mb-8" data-id="element-10324">
              <h2 className="text-xl font-bold mb-4" data-id="element-10325">
                11. Third-Party Services and Links
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-10326">
                Our Services may contain links to third-party websites,
                services, or resources. We do not endorse and are not
                responsible for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10327">
                <li data-id="element-10328">
                  The availability, accuracy, or content of third-party services
                </li>
                <li data-id="element-10329">Third-party privacy practices or terms of service</li>
                <li data-id="element-10330">Any damages or losses caused by third-party services</li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-10331">
                Your interactions with third-party services are solely between
                you and the third party. We encourage you to review the terms
                and privacy policies of any third-party services you access.
              </p>
            </section>

            <section className="mb-8" data-id="element-10332">
              <h2 className="text-xl font-bold mb-4" data-id="element-10333">
                12. Modifications to Services and Terms
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10334">
                12.1 Service Changes
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10335">
                We reserve the right to modify, suspend, or discontinue any
                aspect of the Services at any time, with or without notice. We
                are not liable to you or any third party for any modification,
                suspension, or discontinuation of the Services.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10336">12.2 Terms Changes</h3>
              <p className="text-gray-700 mb-4" data-id="element-10337">
                We may revise these Terms at any time. Material changes will be
                notified through:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-10338">
                <li data-id="element-10339">Email notification to your registered email address</li>
                <li data-id="element-10340">Prominent notice on our website or application</li>
                <li data-id="element-10341">Updated "Last Updated" date at the top of these Terms</li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-10342">
                Your continued use of the Services after changes become
                effective constitutes acceptance of the revised Terms. If you do
                not agree to the changes, you must stop using the Services and
                delete your account.
              </p>
            </section>

            <section className="mb-8" data-id="element-10343">
              <h2 className="text-xl font-bold mb-4" data-id="element-10344">
                13. Governing Law and Jurisdiction
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-10345">
                These Terms shall be governed by and construed in accordance
                with the laws of the United States, without regard to its
                conflict of law provisions.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-10346">
                You agree to submit to the personal and exclusive jurisdiction
                of the courts located within the United States for the
                resolution of any disputes arising from these Terms or your use
                of the Services.
              </p>
            </section>

            <section className="mb-8" data-id="element-10347">
              <h2 className="text-xl font-bold mb-4" data-id="element-10348">
                14. Dispute Resolution and Arbitration
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10349">
                14.1 Informal Resolution
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10350">
                Before filing a claim, you agree to contact us at
                toxicdetector25@gmail.com to attempt to resolve the dispute
                informally. We will attempt to resolve the dispute within 60
                days.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10351">
                14.2 Binding Arbitration
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10352">
                If we cannot resolve the dispute informally, any dispute arising
                from these Terms or the Services shall be resolved through
                binding arbitration in accordance with the American Arbitration
                Association's rules, except as modified by these Terms.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10353">
                14.3 Class Action Waiver
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10354">
                <strong data-id="element-10355">
                  YOU AND TOXICDETECTOR AGREE THAT EACH MAY BRING CLAIMS AGAINST
                  THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A
                  PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR
                  REPRESENTATIVE PROCEEDING.
                </strong>
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10356">14.4 Exceptions</h3>
              <p className="text-gray-700 mb-4" data-id="element-10357">
                Either party may seek injunctive or other equitable relief in
                any court of competent jurisdiction to prevent actual or
                threatened infringement, misappropriation, or violation of
                intellectual property rights.
              </p>
            </section>

            <section className="mb-8" data-id="element-10358">
              <h2 className="text-xl font-bold mb-4" data-id="element-10359">15. General Provisions</h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10360">
                15.1 Entire Agreement
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-10361">
                These Terms, together with our Privacy Policy, constitute the
                entire agreement between you and ToxicDetector regarding the
                Services and supersede all prior agreements and understandings.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10362">15.2 Severability</h3>
              <p className="text-gray-700 mb-4" data-id="element-10363">
                If any provision of these Terms is found to be invalid or
                unenforceable, the remaining provisions will remain in full
                force and effect.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10364">15.3 Waiver</h3>
              <p className="text-gray-700 mb-4" data-id="element-10365">
                Our failure to enforce any right or provision of these Terms
                will not be deemed a waiver of such right or provision.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10366">15.4 Assignment</h3>
              <p className="text-gray-700 mb-4" data-id="element-10367">
                You may not assign or transfer these Terms or your rights
                hereunder without our prior written consent. We may assign these
                Terms without restriction.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10368">15.5 Force Majeure</h3>
              <p className="text-gray-700 mb-4" data-id="element-10369">
                We shall not be liable for any failure or delay in performance
                due to circumstances beyond our reasonable control, including
                acts of God, war, terrorism, riots, natural disasters, or
                government actions.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-10370">15.6 No Agency</h3>
              <p className="text-gray-700 mb-4" data-id="element-10371">
                No agency, partnership, joint venture, or employment
                relationship is created between you and ToxicDetector as a
                result of these Terms or your use of the Services.
              </p>
            </section>

            <section className="mb-8" data-id="element-10372">
              <h2 className="text-xl font-bold mb-4" data-id="element-10373">
                16. Contact Information
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-10374">
                If you have questions about these Terms, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-10375">
                <p className="text-gray-700 mb-2" data-id="element-10376">
                  <strong data-id="element-10377">Email:</strong> toxicdetector25@gmail.com
                </p>
                <p className="text-gray-700 mb-2" data-id="element-10378">
                  <strong data-id="element-10379">Support:</strong> support@toxicdetector.com
                </p>
                <p className="text-gray-700" data-id="element-10380">
                  <strong data-id="element-10381">General Inquiries:</strong> toxicdetector25@gmail.com
                </p>
              </div>
            </section>

            <section className="mb-8 bg-red-50 p-6 rounded-xl border border-red-100" data-id="element-10382">
              <h2 className="text-xl font-bold mb-4 text-red-900" data-id="element-10383">
                17. Liability & Usage Disclosure (Safe-Scalability)
              </h2>
              <p className="text-gray-800 mb-4" data-id="element-10384">
                ToxicDetector.ai is an AI-powered linguistic analysis tool
                designed for educational, entertainment, and self-validation
                purposes only.
              </p>
              <ul className="list-disc pl-6 text-gray-800 mb-4 space-y-2" data-id="element-10385">
                <li data-id="element-10386">
                  <strong data-id="element-10387">Not Clinical Advice:</strong> While our algorithms are
                  trained on behavioral patterns, ToxicDetector does not provide
                  medical, psychological, or psychiatric diagnoses. It is not a
                  substitute for professional therapy or mental health
                  counseling.
                </li>
                <li data-id="element-10388">
                  <strong data-id="element-10389">Not Legal Evidence:</strong> The "Forensic Dossier"
                  and "Toxicity Scores" are digital interpretations of text
                  patterns and are not certified for use as primary evidence in
                  legal proceedings or divorce litigation.
                </li>
                <li data-id="element-10390">
                  <strong data-id="element-10391">Data Privacy:</strong> We utilize AES-256 encryption.
                  By using this tool, you confirm you have the right to analyze
                  the uploaded text. ToxicDetector is not responsible for
                  interpersonal outcomes resulting from the use of our reports.
                </li>
                <li data-id="element-10392">
                  <strong data-id="element-10393">Accuracy:</strong> AI is a pattern-recognition tool.
                  Results are probabilistic, not absolute truths.
                </li>
              </ul>
            </section>

            <section className="mb-8 bg-gray-50 p-6 rounded-xl border border-gray-200" data-id="element-10394">
              <h2 className="text-xl font-bold mb-4" data-id="element-10395">
                18. Refund Policy: Digital Forensic Scans
              </h2>
              <p className="text-gray-800 font-semibold mb-2" data-id="element-10396">
                Final Sale Notice: Due to the immediate nature of AI-driven
                linguistic analysis, all completed scans are Final and
                Non-Refundable.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-10397">
                Once the Forensic Engine processes your data and generates your
                Toxicity Score, the digital value—derived from our proprietary
                algorithms and server processing—has been fully delivered. We do
                not offer refunds based on the 'result' of a scan (e.g., if you
                disagree with a 'Saint' or 'Toxic' rating), as the AI provides
                an objective analysis based on the text provided.
              </p>
              <p className="text-gray-700" data-id="element-10398">
                If you experience a technical error where a report is not
                generated, please contact Support@ToxicDetector.com for a manual
                re-run.
              </p>
            </section>

            <section className="mb-8" data-id="element-10399">
              <h2 className="text-xl font-bold mb-4" data-id="element-10400">19. Acknowledgment</h2>
              <p className="text-gray-700 mb-4" data-id="element-10401">
                BY USING TOXICDETECTOR, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE
                TERMS OF SERVICE, UNDERSTAND THEM, AND AGREE TO BE BOUND BY
                THEM. IF YOU DO NOT AGREE TO THESE TERMS, YOU MUST NOT USE OUR
                SERVICES.
              </p>
            </section>

            <div className="border-t border-gray-200 pt-6 mt-8" data-id="element-10402">
              <p className="text-gray-600 text-sm text-center" data-id="element-10403">
                © {new Date().getFullYear()} ToxicDetector. All rights
                reserved.
              </p>
              <p className="text-gray-500 text-xs text-center mt-2" data-id="element-10404">
                These Terms of Service were last updated on{' '}
                {new Date().toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>;
}